import { ToolbarButton, TableDataRequest, Modal } from '@fl/cmsch-fe-library';
import React, { FC, memo, useMemo } from 'react';
import { Opt } from 'ts-opt';
import { BreedingFemale } from 'api/gen/BreedingFemale';
import { ConnectedTable } from 'utils/tables';
import { ExportPdfForm } from '../export-pdf-form';
import { getCowBrowserColumns } from './columns';
import { simpleAnimalBrowserSelector } from '../../model';
import { useAnimalsBrowserBasicSetup } from '../../utils/use-animals-browser-basic-setup';
interface Props {
  /** Shows the print export button as loading while the print request runs. */
  printInProgress: boolean;
  /** Shows the CSV export button as loading while the CSV export runs. */
  exportInProgress: boolean;
  /** Fetches the breeding-female table data for the selected stable. */
  getCows(tableDataRequest: Opt<TableDataRequest<BreedingFemale>>): void;
}
const CowBrowserTableBase: FC<Props> = ({
  printInProgress,
  exportInProgress,
  getCows
}) => {
  const {
    tCommon,
    t,
    disabledExportTitle,
    exportDisabled,
    femalesForExport,
    contentForPdfExportIsFilled,
    handleSubmitModal,
    handleCloseModal,
    handleOpenExportPdfModal,
    exportToCSV,
    handleMotherLink
  } = useAnimalsBrowserBasicSetup('BREEDING_FEMALES');
  const headerButtons: Array<ToolbarButton> = useMemo(() => [{
    disabled: exportDisabled,
    onClick: handleOpenExportPdfModal,
    title: t('printIk'),
    disabledTitle: disabledExportTitle,
    id: 'exportBreedingFemalesPdf',
    role: 'export',
    loading: printInProgress
  }, {
    disabled: exportDisabled,
    onClick: exportToCSV,
    title: tCommon('downloadCsv'),
    disabledTitle: disabledExportTitle,
    id: 'exportCows',
    role: 'export',
    loading: exportInProgress
  }], [exportDisabled, handleOpenExportPdfModal, t, disabledExportTitle, printInProgress, exportInProgress, exportToCSV, tCommon]);
  return <>
            <ConnectedTable tableId="cowBrowser" columns={getCowBrowserColumns(handleMotherLink)} onFetchTableData={getCows} headerButtons={headerButtons} rowDataPageSelector={simpleAnimalBrowserSelector.cowBrowserPage} enableTableSettings enableTableUserSettings paginationPosition="both" withoutVerticalScrollbar lazyLoadingLimit={20} showFulltextFilter data-sentry-element="ConnectedTable" data-sentry-source-file="index.tsx" />
            <Modal title={t('printIk')} visible={contentForPdfExportIsFilled} onCancel={handleCloseModal} footer={null} data-sentry-element="Modal" data-sentry-source-file="index.tsx">
                <ExportPdfForm tableContainsFemales={femalesForExport.length > 0} tableContainsBulls={false} onCancel={handleCloseModal} onSubmit={handleSubmitModal} isFromCows data-sentry-element="ExportPdfForm" data-sentry-source-file="index.tsx" />
            </Modal>
        </>;
};

/**
 * Renders the breeding-female table plus the print/export modal for the basic cow browser.
 *
 * @param props Table fetch callback and export loading state.
 * @returns The cow-browser table with its export modal.
 *
 * @example
 * <CowBrowserTable
 *   printInProgress={false}
 *   exportInProgress={false}
 *   getCows={fetchCows}
 * />
 */
export const CowBrowserTable = memo(CowBrowserTableBase);